import { StageType } from '@/store/types/StageType'
import type { V2WorkflowStageInstancePayload } from '@/store/types/V2WorkflowStageInstancePayload'

/**
 * Checks if a user is assigned to a specific stage instance.
 *
 * Assignment can happen in two ways:
 * 1. Direct assignment via user_id
 * 2. Assignment through assignable_users (for Annotate and Review stages)
 *
 * @param instance - The stage instance to check
 * @param userId - The user ID to check assignment for
 * @returns true if the user is assigned to this stage instance
 */
export const isUserAssignedToStageInstance = (
  instance: V2WorkflowStageInstancePayload,
  userId: number,
): boolean => {
  // Check if directly assigned to current user
  if (instance.user_id === userId) {
    return true
  }

  // Check assignable_users for Annotate and Review stages
  if (instance.stage.type === StageType.Annotate || instance.stage.type === StageType.Review) {
    return instance.stage.assignable_users.some(({ user_id }) => user_id === userId)
  }

  return false
}

/**
 * Checks if a user is assigned to any PBS (Parallel Blind Stage) sub-stages.
 *
 * @param currentStageInstances - All current stage instances
 * @param selectedStageId - The selected PBS stage ID
 * @param userId - The user ID to check assignment for
 * @returns true if the user is assigned to any PBS sub-stages
 */
export const isUserAssignedToPBSSubStages = (
  currentStageInstances: V2WorkflowStageInstancePayload[],
  selectedStageId: string,
  userId: number,
): boolean =>
  currentStageInstances.some((instance) => {
    // Check if this is a PBS sub-stage (has parent_stage_id matching the selected PBS stage)
    if (instance.parent_stage_id !== selectedStageId) {
      return false
    }

    return isUserAssignedToStageInstance(instance, userId)
  })
